import Provider from '@/enums/Provider';
import IDescription from '@/interfaces/IDescription';
import IImage from '@/interfaces/IImage';
import UrlTypes from '@/enums/UrlTypes';
import { useNotificationStore } from '@/store/notificationStore';
import { useActiveUrlStore } from '@/store/activeUrlStore';
import { extractImagesFromHtml } from '@/helpers/images/extractImagesFromHtml';
import { processHtml } from '@/helpers/html/htmlProcessor';
import { processImages } from '@/helpers/images/imageProcessor';
import { extractIOPageDescription } from './extractIOPageDescription';
import { extractERSPageDescription } from './extractERSPageDescription';
import { extractKBSPageDescription } from './extractKBSPageDescription';

/**
 * Extracts and processes a page's description:
 * 1) provider-specific HTML extraction
 * 2) raw image extraction
 * 3) HTML cleaning via htmlProcessor
 * 4) image URL normalization via imageProcessor
 */
export function extractPageDescription(
    htmlSource: string,
    pathname: string,
    provider: Provider
): IDescription {
    const notification = useNotificationStore();
    const baseUrl = useActiveUrlStore().activeUrl;
    let rawHtml = '';

    switch (provider) {
        case Provider.KBS:
            rawHtml = extractKBSPageDescription(htmlSource);
            break;
        case Provider.IO:
            rawHtml = extractIOPageDescription(htmlSource);
            break;
        case Provider.ERS:
            rawHtml = extractERSPageDescription(htmlSource);
            break;
        default:
            notification.addErrorNotification(
                `Unsupported provider: ${provider} for URL: ${pathname}`
            );
            return { pathname, descriptionHtml: '', images: [] };
    }

    // Extract raw images without normalization
    const rawImages: IImage[] = extractImagesFromHtml(
        rawHtml,
        pathname,
        UrlTypes.PAGE_DESCRIPTION
    );

    // Clean HTML (URL fixes + class stripping)
    const descriptionHtml = processHtml(rawHtml, rawImages, baseUrl);

    // Normalize image URLs
    const images = processImages(rawImages, baseUrl);

    return { pathname, descriptionHtml, images };
}
